class ApiClient {
  private baseURL = "/api";

  async get<T = any>(url: string): Promise<T> {
    const token = localStorage.getItem("auth_token");
    const response = await fetch(`${this.baseURL}${url}`, {
      method: "GET",
      credentials: "include",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json"
      },
    });

    return this.handleResponse<T>(response);
  }

  async post<T = any>(endpoint: string, data: any): Promise<T> {
    const token = localStorage.getItem("auth_token");
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    return this.handleResponse<T>(response);
  }

  async put<T = any>(endpoint: string, data: any): Promise<T> {
    const token = localStorage.getItem("auth_token");
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    return this.handleResponse<T>(response);
  }

  async patch<T = any>(endpoint: string, data: any): Promise<T> {
    const token = localStorage.getItem("auth_token");
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: "PATCH",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    return this.handleResponse<T>(response);
  }

  async delete<T = any>(endpoint: string): Promise<T> {
    const token = localStorage.getItem("auth_token");
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: "DELETE",
      credentials: "include",
      headers: {
        "Authorization": `Bearer ${token}`,
      },
    });

    return this.handleResponse<T>(response);
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    // Handle no content responses
    if (response.status === 204) {
      return null as T;
    }

    // Handle unauthorized responses
    if (response.status === 401) {
      localStorage.removeItem("auth_token");
      window.location.href = "/login";
      throw new Error("Unauthorized");
    }

    if (!response.ok) {
      throw new Error(`API Error: ${response.statusText}`);
    }

    // Check if response has content
    const text = await response.text();
    if (!text || text.trim() === '') {
      return null as T;
    }

    let responseData;
    try {
      responseData = JSON.parse(text);
    } catch (e) {
      console.error("Failed to parse JSON:", text);
      throw new Error("Invalid JSON response from server");
    }

    // Handle null responses
    if (responseData === null) {
      return null as T;
    }

    // Backend returns { success: true, data: ... } or { success: false, error: ... }
    if (responseData.error) {
      throw new Error(responseData.error);
    }

    // Check if this looks like a wrapper response (has success/data structure)
    // vs a direct object response (has id/steps structure)
    const isWrapperResponse = responseData.success !== undefined || 
                            (responseData.data !== undefined && !responseData.id);
    
    if (isWrapperResponse) {
      // This is a wrapped response: { success: true, data: actualData }
      return responseData.data !== undefined ? responseData.data : responseData;
    } 
    
    // This is a direct response: { id: "...", steps: [...], data: {...} }
    return responseData;
  }
}

// Runs-specific API methods
class RunsApi extends ApiClient {
  async getRuns(
    testId: string,
    options: {
      offset?: number;
      limit?: number;
      runId?: string;
      state?: 'All' | 'SUCCESS' | 'FAILURE' | 'RUNNING';
      dateRange?: 'all' | 'today' | 'yesterday' | 'last30' | 'specific' | 'range';
      specificDate?: string;
      startDate?: string;
      endDate?: string;
    } = {}
  ) {
    const searchParams = new URLSearchParams();
    
    if (options.offset !== undefined) searchParams.set('offset', options.offset.toString());
    if (options.limit !== undefined) searchParams.set('limit', options.limit.toString());
    if (options.runId) searchParams.set('runId', options.runId);
    if (options.state) searchParams.set('state', options.state);
    if (options.dateRange) searchParams.set('dateRange', options.dateRange);
    if (options.specificDate) searchParams.set('specificDate', options.specificDate);
    if (options.startDate) searchParams.set('startDate', options.startDate);
    if (options.endDate) searchParams.set('endDate', options.endDate);
    
    const queryString = searchParams.toString() ? `?${searchParams.toString()}` : '';
    return this.get(`/test/${testId}/runs${queryString}`);
  }

  async getRunsByGroup(testId: string, groupId: string) {
    return this.get(`/test/${testId}/runs/group/${groupId}`);
  }

  async getRunDetails(testId: string, runId: string) {
    return this.get(`/test/${testId}/runs/${runId}`);
  }

  async createRun(testId: string, data: { numberOfRuns: string; automatedExecution?: boolean }) {
    return this.post(`/test/${testId}/runs`, data);
  }

  async cancelRunGroup(testId: string, groupId: string) {
    return this.post(`/test/${testId}/runs/group/${groupId}/cancel`, {});
  }

  async rerunRunGroup(testId: string, groupId: string) {
    return this.post(`/test/${testId}/runs/group/${groupId}/rerun`, {});
  }

  async deleteRunGroup(testId: string, groupId: string) {
    return this.delete(`/test/${testId}/runs/group/${groupId}`);
  }

  async deleteRun(testId: string, runId: string) {
    return this.delete(`/test/${testId}/runs/${runId}`);
  }
}

export const api = new RunsApi();
